import AvatarChip from '../../../shared/avatar-chip';
import { useEffect, useState } from 'react';
import { setNewAdmin } from '../../../api/administration-api';
import UsersAutocomplete from '../../../shared/users-autocomplete';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { UserKeys } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { useStore } from '../../state/zustand-store';
import { ModuleList } from '../../../shared/analytics/module-list';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { emailRegex } from '../../../utils/regular-expressions';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SharedTabHeader from '../../../shared/shared-tab-header';

export default function SetGpanelAdmin() {
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');

  const [errorMessage, setErrorMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();

  const { administrationRoles, changeLogRoles, adminEmail } = useUserSettings();

  useEffect(() => {
    if (!autoCompleteResponse) {
      setErrorMessage('');
      return;
    }
    if (autoCompleteResponse === adminEmail) {
      setErrorMessage('New admin email cannot be the same as current admin');
      return;
    }
    if (!emailRegex.test(autoCompleteResponse)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }
    setErrorMessage('');
  }, [autoCompleteResponse, adminEmail]);

  const { mutate, isPending } = useMutation({
    mutationFn: () => setNewAdmin(autoCompleteResponse, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('gPanel Admin'),
      });

      queryClient.invalidateQueries({ queryKey: [UserKeys.UserSettings] });

      setAutoCompleteResponse('');
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating('gPanel Admin'),
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.settings.update_gpanel_admin, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.settings,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const hasUpdatePermission = isAbleToSeeUIElement(
    administrationRoles,
    AdministrationRoles.UpdateGpanelAdmin.value
  );

  return (
    <Stack gap={2}>
      <SharedTabHeader title="Set gPanel Admin" />
      <Box sx={{ px: 1 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          The gPanel admin account will be used to authenticate all Google API
          calls made within gPanel. We recommend using a service account within
          your domain that has Super Admin access and within a Google
          organization that has all services enabled. This ensures uninterrupted
          service within gPanel.
        </Typography>
        <Stack flexDirection="row" gap={2}>
          <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
            Current Admin:
          </Typography>
          <AvatarChip email={adminEmail} size="small" type={'User'} />
        </Stack>
        <Tooltip
          arrow
          title={
            !hasUpdatePermission
              ? `You need the "Can update gPanel Admin" permission to make changes`
              : ''
          }
        >
          <Stack direction="row" gap={1} sx={{ mt: 2 }}>
            <Box sx={{ width: 400 }}>
              <UsersAutocomplete
                freeSolo
                autocompleteValue={autoCompleteResponse}
                setAutoCompleteValue={setAutoCompleteResponse}
                disabled={!hasUpdatePermission}
                errors={!!errorMessage}
                helperText={errorMessage}
              />
            </Box>
            <Button
              variant="contained"
              onClick={() => {
                if (
                  isAbleToSeeUIElement(
                    changeLogRoles,
                    AdministrationRoles.UpdateGpanelAdmin.value
                  )
                ) {
                  setOpenChangeLogDialog(true);
                } else {
                  mutate();
                }
              }}
              loading={isPending}
              disabled={
                isPending ||
                !hasUpdatePermission ||
                !!errorMessage ||
                autoCompleteResponse === ''
              }
              sx={{ height: 40 }}
            >
              Save
            </Button>
          </Stack>
        </Tooltip>
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={mutate}
          mutateLoading={isPending}
        />
      </Box>
    </Stack>
  );
}
